import pluralize from 'pluralize';

import { FacilityPayload } from '@/hooks/useFacilitiesQuery';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';

interface FacilityTypeDisplayConfig {
  groupLabel: string;
  unit: string;
}

const facilityTypeDisplayConfigMap: Partial<
  Record<FacilityFacilityTypeEnum, FacilityTypeDisplayConfig>
> = {
  [FacilityFacilityTypeEnum.SkilledNursing]: {
    groupLabel: 'All Skilled Nursing Facilities',
    unit: 'Facility'
  },
  [FacilityFacilityTypeEnum.HomeHealth]: {
    groupLabel: 'All Home Health Agencies',
    unit: 'Agency'
  },
  [FacilityFacilityTypeEnum.CriticalCare]: {
    groupLabel: 'All Critical Care Facilities',
    unit: 'Facility'
  },
  [FacilityFacilityTypeEnum.AssistedLiving]: {
    groupLabel: 'All Assisted Living Facilities',
    unit: 'Facility'
  },
  [FacilityFacilityTypeEnum.LongTermCareHospital]: {
    groupLabel: 'All Long-Term Care Hospitals',
    unit: 'Hospital'
  },
  [FacilityFacilityTypeEnum.Hospice]: {
    groupLabel: 'All Hospice Facilities',
    unit: 'Facility'
  }
};

const defaultFacilityTypeDisplayConfig: FacilityTypeDisplayConfig = {
  groupLabel: 'All Facilities',
  unit: 'Facility'
};

const getFacilityTypeDisplayConfig = (
  facilityType: FacilityFacilityTypeEnum
): FacilityTypeDisplayConfig =>
  facilityTypeDisplayConfigMap[facilityType] ??
  defaultFacilityTypeDisplayConfig;

export const formatFacilityTypeCountLabel = (
  facilityType: FacilityFacilityTypeEnum,
  count: number
) => {
  const { unit } = getFacilityTypeDisplayConfig(facilityType);
  return `${count} ${pluralize(unit, count)}`;
};

export const getGenericAllLabelForType = (
  facilityType: FacilityFacilityTypeEnum
) => {
  const { unit } = getFacilityTypeDisplayConfig(facilityType);
  return `All ${pluralize(unit)}`;
};

export interface FacilityTypeGroupOption {
  facilityIds: string[];
  facilityType: FacilityFacilityTypeEnum;
  label: string;
  subtitle: string;
}

export const getFacilityTypeGroupOptions = (
  facilities: FacilityPayload[],
  accessibleFacilityIds: ReadonlySet<string>
): FacilityTypeGroupOption[] => {
  const facilityIdsByType = facilities.reduce<
    Partial<Record<FacilityFacilityTypeEnum, string[]>>
  >((acc, facility) => {
    if (!facility.facility_type || !accessibleFacilityIds.has(facility.id)) {
      return acc;
    }
    acc[facility.facility_type] = [
      ...(acc[facility.facility_type] ?? []),
      facility.id
    ];
    return acc;
  }, {});

  return Object.entries(facilityIdsByType)
    .map(([facilityType, facilityIds]) => {
      const normalizedFacilityType = facilityType as FacilityFacilityTypeEnum;
      const displayConfig = getFacilityTypeDisplayConfig(
        normalizedFacilityType
      );
      return {
        facilityIds: facilityIds ?? [],
        facilityType: normalizedFacilityType,
        label: displayConfig.groupLabel,
        subtitle: formatFacilityTypeCountLabel(
          normalizedFacilityType,
          facilityIds?.length ?? 0
        )
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};
